/**
 * Render a PDF page to a JPEG data URL and optionally transcribe it with a
 * vision LLM via OpenRouter.
 */
import type { PDFPageProxy } from "pdfjs-dist";
import {
  ContentPart,
  DEFAULT_VISION_MODEL,
  RunPromptOptions,
} from "./aiCommon";
import { runChatOpenRouter } from "./openRouterClient";

const RENDER_SCALE = 1.75;
const JPEG_QUALITY = 0.72;
/** Longer edge target when attaching images to analysis prompts (~1600 px). */
const ATTACH_MAX_EDGE = 1600;

export const PAGE_TRANSCRIBE_PROMPT = `Extract all visible text from this PDF page image. Preserve reading order. Turn any tables into GitHub-flavoured markdown tables. Do not invent values that are not visible. Return only the transcribed content — no preamble.`;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read blob"));
    reader.readAsDataURL(blob);
  });
}

/**
 * Render a pdf.js page to a JPEG data URL.
 * Returns null when the canvas has negligible area (blank / failed render).
 */
export async function renderPdfPageToJpeg(
  page: PDFPageProxy,
  options: { scale?: number; maxEdge?: number; quality?: number } = {}
): Promise<string | null> {
  const scale = options.scale ?? RENDER_SCALE;
  const quality = options.quality ?? JPEG_QUALITY;
  const viewport = page.getViewport({ scale });

  let { width, height } = viewport;
  const { maxEdge } = options;
  if (maxEdge && Math.max(width, height) > maxEdge) {
    const factor = maxEdge / Math.max(width, height);
    width = Math.round(width * factor);
    height = Math.round(height * factor);
  }

  if (width < 16 || height < 16) return null;

  // Prefer OffscreenCanvas when available (workers / modern browsers);
  // fall back to a DOM canvas for older environments and Jest.
  const canvas: HTMLCanvasElement | OffscreenCanvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(width, height)
      : (() => {
          const el = document.createElement("canvas");
          el.width = width;
          el.height = height;
          return el;
        })();

  if ("width" in canvas) {
    (canvas as HTMLCanvasElement).width = width;
    (canvas as HTMLCanvasElement).height = height;
  }

  const context = canvas.getContext("2d") as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!context) return null;

  const renderViewport =
    width === viewport.width && height === viewport.height
      ? viewport
      : page.getViewport({
          scale: scale * (width / viewport.width),
        });

  await page.render({
    canvasContext: context as CanvasRenderingContext2D,
    viewport: renderViewport,
  }).promise;

  if ("convertToBlob" in canvas) {
    const blob = await (canvas as OffscreenCanvas).convertToBlob({
      type: "image/jpeg",
      quality,
    });
    return blobToDataUrl(blob);
  }

  return (canvas as HTMLCanvasElement).toDataURL("image/jpeg", quality);
}

/**
 * Render a page and resize so the longer edge is ~ATTACH_MAX_EDGE for
 * attach-images mode (keeps request size sane).
 */
export async function renderPdfPageForAttach(
  page: PDFPageProxy
): Promise<string | null> {
  return renderPdfPageToJpeg(page, {
    scale: RENDER_SCALE,
    maxEdge: ATTACH_MAX_EDGE,
    quality: JPEG_QUALITY,
  });
}

/**
 * Transcribe a page image with a vision LLM. Returns empty string on failure.
 */
export async function transcribePageImage(
  dataUrl: string,
  options: RunPromptOptions = {}
): Promise<string> {
  const content: ContentPart[] = [
    { type: "text", text: PAGE_TRANSCRIBE_PROMPT },
    { type: "image_url", image_url: { url: dataUrl } },
  ];
  const result = await runChatOpenRouter(
    [
      {
        role: "system",
        content:
          "You are an accurate OCR assistant for regulatory PDF pages. Transcribe exactly what you see.",
      },
      { role: "user", content },
    ],
    {
      ...options,
      model: options.model || DEFAULT_VISION_MODEL,
      temperature:
        typeof options.temperature === "number" ? options.temperature : 0.2,
    }
  );
  return (result.text || "").trim();
}

/**
 * Render + transcribe a pdf.js page in one step.
 */
export async function transcribePdfPage(
  page: PDFPageProxy,
  options: RunPromptOptions = {}
): Promise<string> {
  const dataUrl = await renderPdfPageToJpeg(page);
  if (!dataUrl) return "";
  return transcribePageImage(dataUrl, options);
}
